import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import queryClient from 'api/queryClient'
import { fetchFeedNewItems } from 'api/api'
import type { FeedNewItemsAuthor, FeedNewItemsItem } from 'api/api'
import { getQuestsQueryKey } from 'refactor/hooks/api/useGetQuests'
import { subscribePublicFeedTicks, subscribeUserFeedTicks } from 'state/feedTicks'
import { radioNowFeed } from 'state/radioNowFeed'
import { followRadioStream } from './followRadioStream'
import { getCachedQuest } from 'state/cache'
import type { PanelState } from 'state/PanelState'
import { withDepth } from 'components/filters/useStreamFilters'
import isNewItemsEligible, { isLiveRadioStream } from './feedNewItemsEligibility'

// Debounce absorbs bursts of ticks and the fanout→ES indexing gap on the
// public side (docs/feed-new-items-pill.md).
const TICK_DEBOUNCE_MS = 4000
const FOCUS_MIN_INTERVAL_MS = 15_000

const MAX_PILL_AUTHORS = 3

interface FeedNewItemsState {
    newItemsCount: number
    newItemsCapped: boolean
    hasNewItems: boolean
    // First MAX_PILL_AUTHORS distinct authors among the unseen items, in feed
    // order — the pill's avatar pile.
    newItemsAuthors: FeedNewItemsAuthor[]
}

export default function useFeedNewItems(
    panel: PanelState,
    options: {
        active?: boolean
        scrollContainerRef?: { current: { scrollTop: number } | null }
    } = {},
): FeedNewItemsState {
    const filter = panel.filter
    const loggedIn = Boolean(gon?.currentUser)
    const isRadioStream = isLiveRadioStream(filter)
    const eligible = isRadioStream ? options.active !== false : isNewItemsEligible(filter, loggedIn)
    // Canonical key, not filter identity: panel filters churn identity without
    // changing meaning, and resubscribing the cable on every churn is waste.
    // QuestStream always mounts feeds with their resolved depth. Reuse that
    // exact key here so refresh-success events and rendered rows are visible
    // to the badge state machine.
    const queryKey = useMemo(
        () => (eligible && filter ? getQuestsQueryKey(withDepth(filter)) : null),
        [eligible, filter],
    )
    const filterKey = useMemo(() => (queryKey ? JSON.stringify(queryKey) : null), [queryKey])

    const [newItems, setNewItems] = useState<FeedNewItemsItem[]>([])
    const [capped, setCapped] = useState(false)
    const [hasActivity, setHasActivity] = useState(false)

    const watermarkRef = useRef<number | null>(null)
    const lastFetchAtRef = useRef(0)
    const lastHeadSignatureRef = useRef<string | null>(null)
    const probeHeadInFlightRef = useRef<object | null>(null)
    const probeHeadPendingRef = useRef(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const generationRef = useRef(0)

    const renderedQuestIds = useCallback((): Set<string> => {
        if (!queryKey) return new Set()
        const data = queryClient.getQueryData(queryKey) as
            | { pages?: Array<{ quests?: Array<{ id?: unknown }> }> }
            | undefined
        const quests = data?.pages?.flatMap(page => page?.quests || []) || []
        return new Set(quests.map(quest => String(quest?.id)))
    }, [queryKey])

    const currentHeadSignature = useCallback((): string | null => {
        if (!queryKey) return null
        const data = queryClient.getQueryData(queryKey) as
            | { pages?: Array<{ quests?: Array<{ id?: unknown }> }> }
            | undefined
        const head = data?.pages?.[0]?.quests?.[0]?.id
        if (head == null) return null

        const headId = String(head)
        const cachedHead = getCachedQuest(headId) as { last_acted_time?: unknown } | undefined
        return JSON.stringify([headId, cachedHead?.last_acted_time ?? null])
    }, [queryKey])

    const probeHead = useCallback(async () => {
        if (!eligible) return
        if (probeHeadInFlightRef.current) {
            probeHeadPendingRef.current = true
            return
        }

        const probeToken = {}
        probeHeadInFlightRef.current = probeToken
        try {
            do {
                probeHeadPendingRef.current = false
                const generation = generationRef.current
                const headSignature = currentHeadSignature()
                lastFetchAtRef.current = Date.now()
                const res = await fetchFeedNewItems(filter, null)
                if (generation !== generationRef.current) return
                if (res?.watermark != null) {
                    watermarkRef.current = res.watermark
                    lastHeadSignatureRef.current = headSignature
                    setNewItems([])
                    setCapped(false)
                    setHasActivity(false)
                }
            } while (probeHeadInFlightRef.current === probeToken && probeHeadPendingRef.current)
        } finally {
            if (probeHeadInFlightRef.current === probeToken) {
                probeHeadInFlightRef.current = null
            }
        }
    }, [eligible, filter, currentHeadSignature])

    const poll = useCallback(async () => {
        if (!eligible) return
        if (watermarkRef.current == null) {
            probeHead()
            return
        }
        lastFetchAtRef.current = Date.now()
        const generation = generationRef.current
        const res = await fetchFeedNewItems(filter, watermarkRef.current)
        if (generation !== generationRef.current) return
        if (!res?.quest_items) return
        const rendered = renderedQuestIds()
        const unseen = res.quest_items
            .filter(item => item?.id != null)
            .map(item => ({ ...item, id: String(item.id) }))
            .filter(item => !rendered.has(item.id))
        setNewItems(unseen)
        setCapped(Boolean(res.capped))
        setHasActivity(res.quest_items.length > 0)
    }, [eligible, filter, probeHead, renderedQuestIds])

    // Latest-callback refs so the subscription effects can key on the stable
    // filterKey instead of callback identity.
    const probeHeadRef = useRef(probeHead)
    probeHeadRef.current = probeHead
    const pollRef = useRef(poll)
    pollRef.current = poll
    const renderedQuestIdsRef = useRef(renderedQuestIds)
    renderedQuestIdsRef.current = renderedQuestIds
    const currentHeadSignatureRef = useRef(currentHeadSignature)
    currentHeadSignatureRef.current = currentHeadSignature
    const followingRef = useRef(Boolean(filter?.following))
    followingRef.current = Boolean(filter?.following)
    const optionsRef = useRef(options)
    optionsRef.current = options
    const panelRef = useRef(panel)
    panelRef.current = panel

    useEffect(() => {
        if (!eligible || !filterKey) return

        generationRef.current += 1
        watermarkRef.current = null
        setNewItems([])
        setCapped(false)
        setHasActivity(false)
        if (isRadioStream) {
            return followRadioStream({
                feed: radioNowFeed,
                client: queryClient,
                queryKey: JSON.parse(filterKey),
                isAtTop: () =>
                    (optionsRef.current.scrollContainerRef?.current?.scrollTop ?? 0) <= 1,
                refresh: () => panelRef.current.triggerStreamRefresh({ smoothScroll: false }),
                onNewItems: setNewItems,
            })
        }
        // With the feed already loaded (a warm cache), take the baseline now. Otherwise
        // the feed query's first success probes below; probing here too fetched the
        // same watermark twice on every cold mount.
        if (queryClient.getQueryData(queryKey) !== undefined) probeHeadRef.current()

        const onTick = () => {
            if (debounceRef.current) return
            debounceRef.current = setTimeout(() => {
                debounceRef.current = null
                pollRef.current()
            }, TICK_DEBOUNCE_MS)
        }
        const unsubscribeTicks = followingRef.current
            ? subscribeUserFeedTicks(onTick)
            : subscribePublicFeedTicks(onTick)

        // Cable connections die in backgrounded tabs and on mobile: returning
        // to the app re-checks once, throttled.
        const onMaybeStale = () => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
            if (Date.now() - lastFetchAtRef.current < FOCUS_MIN_INTERVAL_MS) return
            pollRef.current()
        }
        document.addEventListener('visibilitychange', onMaybeStale)
        window.addEventListener('online', onMaybeStale)

        return () => {
            generationRef.current += 1
            probeHeadInFlightRef.current = null
            probeHeadPendingRef.current = false
            unsubscribeTicks()
            document.removeEventListener('visibilitychange', onMaybeStale)
            window.removeEventListener('online', onMaybeStale)
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
                debounceRef.current = null
            }
        }
    }, [eligible, filterKey, isRadioStream])

    // Every successful settle of the panel's quests query is either a refresh
    // (head id or activity timestamp changed → adopt a fresh watermark, clear
    // the badge) or pagination/background noise (→ just drop badge ids the
    // list now shows).
    useEffect(() => {
        if (!eligible || !filterKey) return

        if (isRadioStream) return
        const unsubscribe = queryClient.getQueryCache().subscribe(event => {
            if (event?.type !== 'updated') return
            const action = (event as { action?: { type?: string } }).action
            if (action?.type !== 'success') return
            const key = (event as { query?: { queryKey?: unknown } }).query?.queryKey
            if (!key || JSON.stringify(key) !== filterKey) return

            const headSignature = currentHeadSignatureRef.current()
            if (headSignature !== lastHeadSignatureRef.current || watermarkRef.current == null) {
                probeHeadRef.current()
                return
            }
            const rendered = renderedQuestIdsRef.current()
            setNewItems(prev => {
                const remaining = prev.filter(item => !rendered.has(item.id))
                if (remaining.length === 0) {
                    setCapped(false)
                    setHasActivity(false)
                }
                return remaining.length === prev.length ? prev : remaining
            })
        })
        return unsubscribe
    }, [eligible, filterKey, isRadioStream])

    const newItemsAuthors = useMemo(() => {
        const seen = new Set<string>()
        const authors: FeedNewItemsAuthor[] = []
        for (const item of newItems) {
            const user = item.user
            if (!user?.id || seen.has(String(user.id))) continue
            seen.add(String(user.id))
            authors.push(user)
            if (authors.length >= MAX_PILL_AUTHORS) break
        }
        return authors
    }, [newItems])

    return {
        newItemsCount: eligible ? newItems.length : 0,
        newItemsCapped: eligible && capped,
        hasNewItems: eligible && (newItems.length > 0 || hasActivity),
        newItemsAuthors: eligible ? newItemsAuthors : [],
    }
}
